import React, { useState, useEffect, useRef, useMemo } from "react";

import { useTranslation } from "react-i18next";
import classnames from "classnames/dedupe";
import { createPortal } from "react-dom";
import { Badge } from "reactstrap";
import { v4 as uuid } from "uuid";
import axios from "axios";

import { generateAdvancedFiltersOptions } from "../../../helpers/generateAdvancedFilter";
import { checkboxOptions, filterTypeOptions } from "./filterTypeOptions";
import { FilteringType } from "components/prime-data-table/interfaces";
import CustomSpinner from "components/custom-spinner/custom-spinner";
import CustomButton from "components/custom-button/custom-button";
import { useDeepCompareEffect } from "hooks/useDeepCompareEffect";
import { useDeepCompare } from "hooks/useDeepCompare";
import { AdvFiltersDnd } from "./adv-filters-dnd";
import { deepCopy } from "utils/memo/deepCopy";
import { FilterBlock } from "./filter-block";
import { refreshFilters } from "./functions";
import Icon from "components/icon";

import * as interfaces from "./interfaces";

import styles from "./AdvancedFilters.module.scss";

interface IAdvanceFiltersWrapperProps extends interfaces.IAdvanceFilters {
    visible: boolean;
    onClose: () => void;
    additionalParams?: string;
}

export const AdvanceFiltersWrapper = (props: IAdvanceFiltersWrapperProps) => {
    const { advancedFiltersConfig, visible, tableFilters, toggleFilters, onClose } = props;
    const { t } = useTranslation();

    const buttonRef = useRef<HTMLButtonElement>(null);

    const _filterTypeOptions: interfaces.IFilterTypeOptions = useMemo(
        () => ({
            number: filterTypeOptions.number.map((o) => ({ ...o, label: t(o.label) })),
            date: filterTypeOptions.date.map((o) => ({ ...o, label: t(o.label) })),
            text: filterTypeOptions.text.map((o) => ({ ...o, label: t(o.label) })),
            dropdown: filterTypeOptions.dropdown.map((o) => ({ ...o, label: t(o.label) })),
            checkbox: filterTypeOptions.checkbox.map((o) => ({ ...o, label: t(o.label) })),
        }),
        []
    );

    const deepEqualAdvancedFiltersConfig = useDeepCompare(advancedFiltersConfig);

    const advancedFiltersOptions = useMemo(() => {
        return generateAdvancedFiltersOptions(advancedFiltersConfig, t);
    }, [deepEqualAdvancedFiltersConfig]);

    const numberOfFilters = tableFilters?.length;

    return (
        <>
            <CustomButton
                className={styles.button}
                text="Advanced filtering"
                icon={"filter"}
                withoutPerm
                onClick={toggleFilters}
                innerRef={buttonRef}
            >
                {!!numberOfFilters && (
                    <Badge className={styles.filtersBadge} pill>
                        {numberOfFilters}
                    </Badge>
                )}
            </CustomButton>
            {visible && advancedFiltersOptions && (
                <AdvanceFilters
                    {...props}
                    togglePopup={onClose}
                    advancedFiltersOptions={advancedFiltersOptions}
                    filterTypeOptions={_filterTypeOptions}
                    buttonRef={buttonRef}
                    t={t}
                />
            )}
        </>
    );
};

const AdvanceFilters = (props: interfaces.IExtendedAdvanceFilters) => {
    const { onApplyFilters, tableFilters, togglePopup, tableRef, advancedFiltersOptions, t, filterTypeOptions, buttonRef, selectChoices, additionalParams } =
        props;

    const [filters, setFilters] = useState<interfaces.IFilter[]>([]);
    const [filterValueOptions, setFilterValueOptions] = useState<Record<string, unknown> | undefined>({
        additionalOptions: {
            checkboxOptions: checkboxOptions.map((o) => ({ ...o, label: t(o.label) })),
        },
    });

    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (wrapperRef.current?.contains(target) || buttonRef.current === target || buttonRef.current?.contains(target)) {
                return;
            }

            if (
                target.closest(".p-dropdown") ||
                target.closest(".p-dropdown-trigger") ||
                target.closest(".p-dropdown-trigger-icon") ||
                target.closest(".p-dropdown-panel") ||
                target.closest(".p-datepicker") ||
                target.closest(".p-datepicker-panel") ||
                target.closest(".p-component")
            ) {
                return;
            }

            togglePopup(false);
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [togglePopup]);

    useDeepCompareEffect(() => {
        const getOptions = async () => {
            const filtersWithOptions = advancedFiltersOptions.filter((filter) => filter.filterOptionConfig);
            let mappedOptions: interfaces.IMappedOptions = {};
            let retrieveOptionsFromSelectOptions: { [key: string]: ILabelValue<string | number>[] } = {};

            filtersWithOptions?.forEach((o) => {
                const optionsKey: string = o.filterOptionConfig?.appModelPath as string;
                const filterField: string = o.filterOptionConfig?.field as string;

                mappedOptions[optionsKey] = mappedOptions[optionsKey] ? [...mappedOptions[optionsKey], filterField] : [filterField];
            });

            for (const key in selectChoices) {
                retrieveOptionsFromSelectOptions[key] = selectChoices[key];
            }

            let response = { data: {} };
            if (Object.keys(mappedOptions).length != 0) {
                try {
                    response = await axios.put("filters/fields-choices", mappedOptions);
                } catch (e) {
                    console.error(e);
                }
            }

            setFilterValueOptions((prev) => ({
                ...prev,
                ...response.data,
                ...retrieveOptionsFromSelectOptions,
            }));
        };

        getOptions();
        setFilters(
            tableFilters?.length ? tableFilters : [{ id: uuid(), filterField: "", filterType: "", value: "", field: "", operator: "AND" }]
        );
    }, [advancedFiltersOptions, tableFilters]);

    const handleChange = (inputValue: string | undefined | FilteringType, index: number, inputType: interfaces.IInputType) => {
        const _filters = deepCopy(filters);
        const advancedFilter = advancedFiltersOptions.find((f) => f.filterField == inputValue);

        switch (inputType) {
            case "column":
                if (advancedFilter?.filterOptionConfig || advancedFilter?.type === "checkbox") {
                    _filters[index].filterType = "__exact";
                    _filters[index].value = "";
                } else {
                    _filters[index].filterType = "";
                    _filters[index].value = "";
                }

                _filters[index].filterField = inputValue;
                _filters[index].field = advancedFilter?.field;
                break;
            case "condition":
                _filters[index].filterType = inputValue as FilteringType;
                break;
            case "value":
                _filters[index].value = inputValue;
                break;
        }

        setFilters(_filters);
    };

    const handleRefresh = () => {
        return refreshFilters(filters, onApplyFilters, () => {}, additionalParams);
    };

    const handleAddFilter = () => {
        setFilters((prev) => [...prev, { id: uuid(), filterField: "", filterType: "", value: "", field: "", operator: "AND" }]);
    };

    const handleDeleteFilter = (index) => {
        if (filters.length === 1) {
            const empty: interfaces.IFilter[] = [
                { id: filters[0].id, filterField: null, filterType: "", value: "", field: "", operator: "AND" },
            ];
            setFilters(empty);
            refreshFilters(empty, onApplyFilters, () => {}, additionalParams);
        } else {
            const _filters = [...filters];
            _filters.splice(index, 1);
            setFilters(_filters);
            refreshFilters(_filters, onApplyFilters, () => {}, additionalParams);
        }
    };

    const tableBox = tableRef.current?.getBoundingClientRect();

    const BUTTONS_CONTAINER_OFFSET = 50;
    const OFFSET_X = 8;

    return createPortal(
        <div
            className={styles.popup}
            ref={wrapperRef}
            style={{
                top: Number(tableBox?.top) + BUTTONS_CONTAINER_OFFSET,
                right: window.innerWidth - Number(tableBox?.right) - OFFSET_X,
            }}
        >
            {filterValueOptions ? (
                <>
                    <AdvFiltersDnd setFilters={setFilters}>
                        {filters.map((filter, index) => {
                            return (
                                <FilterBlock
                                    key={index}
                                    index={index}
                                    filters={filters}
                                    filter={filter}
                                    onChange={handleChange}
                                    onDelete={handleDeleteFilter}
                                    advancedFiltersOptions={advancedFiltersOptions}
                                    filterTypeOptions={filterTypeOptions}
                                    filterValueOptions={filterValueOptions}
                                    setFilters={setFilters}
                                />
                            );
                        })}
                    </AdvFiltersDnd>

                    <div className={styles.buttonsContainer}>
                        <button className={classnames(styles.addFilterButton, "btn")} onClick={handleAddFilter}>
                            <Icon name="plus" />
                            {t("Add filter")}
                        </button>

                        <button
                            className={classnames(styles.applyFiltersButton, "btn btn-outline-brand")}
                            onClick={handleRefresh}
                            title={t("Filter")}
                        >
                            {t("Apply Filters")}
                        </button>
                    </div>
                </>
            ) : (
                <CustomSpinner />
            )}
        </div>,
        document.body
    );
};
